/**
 * Build the crew "Services Performed Today" checklist from quote/job
 * `available_services`.
 *
 * The quote migration stored every service namespace with `enabled: true`,
 * even when the contract checkbox was off (Spring WorkSelected, Fall Selected,
 * ParkingLotSweeping, etc.). Crews were then able to check off work that was
 * never on the contract. This helper follows the legacy live-log rules in
 * JobLogsHelper.GetServicesByQuote: only emit boxes for services that are
 * actually selected on the quote.
 */

const CHECKED_KEY_ALIASES = {
  gardening_deweeding: 'gardening.deWeeding',
  gardening_edge: 'gardening.edging',
  gardening_soiltilling: 'gardening.soilTilling',
};

export function parseServiceConfig(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

export function isTruthy(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

// Legacy JobLogsHelper lists a gardening sub-service only when its frequency
// is Weekly (1) or Bi-weekly (2). The quote form pre-fills crew work time for
// every sub-service, so minutes alone don't mean it's on the contract. Only
// fall back to minutes when the config has no frequency at all.
function gardeningSubOnContract(minutes, frequency) {
  if (frequency !== undefined && frequency !== null && frequency !== '') {
    return toNumber(frequency) > 0;
  }
  return toNumber(minutes) > 0;
}

function flagIsSet(config, key) {
  return config && config[key] !== undefined && config[key] !== null;
}

function usesInnerContractFlags(config, keys) {
  return keys.some((key) => flagIsSet(config, key));
}

function anyTruthy(config, keys) {
  return keys.some((key) => isTruthy(config?.[key]));
}

/**
 * Legacy import force-set `enabled: true` on every service that had EAV
 * values. When seasonal inner checkboxes exist and are false, `enabled`
 * cannot be trusted as a contract signal (this is how sweeping leaked onto
 * every residential/commercial log).
 */
function enabledFallbackTrusted(services) {
  const poisoned = (cfg, flag) => cfg && isTruthy(cfg.enabled) && cfg[flag] === false;
  return !(
    poisoned(services.spring, 'workSelected')
    || poisoned(services.fall, 'selected')
    || poisoned(services.trimming, 'selected')
  );
}

export function isSpringOnContract(spring) {
  if (!spring) return false;
  if (usesInnerContractFlags(spring, ['workSelected', 'selected'])) {
    return anyTruthy(spring, ['workSelected', 'selected']);
  }
  return isTruthy(spring.enabled);
}

export function isFallOnContract(fall) {
  if (!fall) return false;
  if (usesInnerContractFlags(fall, ['selected', 'workSelected'])) {
    return anyTruthy(fall, ['selected', 'workSelected']);
  }
  return isTruthy(fall.enabled);
}

export function isTrimmingOnContract(trimming) {
  return isFallOnContract(trimming);
}

export function isLawnOnContract(lawn) {
  if (!lawn) return false;
  if (
    isTruthy(lawn.mowing)
    || isTruthy(lawn.bagging)
    || isTruthy(lawn.commonLawnAreaOnly)
    || isTruthy(lawn.trashBagServiceEnabled)
  ) {
    return true;
  }
  // Explicitly-off mowing with no other lawn lines is not on the contract,
  // even if the import marked the lawn namespace enabled.
  if (lawn.mowing === false) return false;
  return isTruthy(lawn.enabled);
}

export function isGardeningOnContract(gardening) {
  if (!gardening) return false;
  if (toNumber(gardening.deWeeding) > 0 || toNumber(gardening.soilTilling) > 0 || toNumber(gardening.edge) > 0) {
    return true;
  }
  return anyTruthy(gardening, [
    'commonOnly',
    'weedSelected',
    'mulchSelected',
    'frontHouseSelected',
    'sideHouseSelected',
    'backHouseSelected',
    'frontCondoSelected',
    'backCondoSelected',
    'commonTreeRingsSelected',
  ]);
}

export function isTurfOnContract(turf) {
  if (!turf) return false;
  if (anyTruthy(turf, ['simple3Selected', 'hybrid5Selected', 'ultra6Selected', 'premium6Selected'])) {
    return true;
  }
  return toNumber(turf.program) > 0;
}

export function isSweepingOnContract(sweeping, customerType, services = {}) {
  const type = String(customerType || '').toLowerCase();
  if (type === 'residential') return false;
  if (isTruthy(services.parkingLot?.sweeping)) return true;
  if (!sweeping) return false;
  if (anyTruthy(sweeping, ['selected', 'workSelected'])) return true;
  if (toNumber(sweeping.sum) > 0) return true;
  if (!enabledFallbackTrusted(services)) return false;
  return isTruthy(sweeping.enabled);
}

function serviceLabel(t, key, fallback) {
  if (typeof t !== 'function') return fallback || key;
  const translated = t(`serviceNames.${key}`, fallback || key);
  if (translated && translated !== `serviceNames.${key}`) return translated;
  return fallback || key;
}

function getTurfProgramLabel(t, turf) {
  if (!turf || typeof turf !== 'object') return null;
  const programMap = {
    simple3Selected: 'turfProgramSimple3',
    hybrid5Selected: 'turfProgramHybrid5',
    ultra6Selected: 'turfProgramUltra6',
    premium6Selected: 'turfProgramPremium6',
  };
  const selected = Object.keys(programMap).find((key) => isTruthy(turf[key]));
  if (selected) return serviceLabel(t, programMap[selected], programMap[selected]);
  const program = toNumber(turf.program);
  if (program === 1) return serviceLabel(t, 'turfProgramSimple3', 'Simple 3');
  if (program === 2) return serviceLabel(t, 'turfProgramHybrid5', 'Hybrid 5');
  if (program === 3) return serviceLabel(t, 'turfProgramPremium6', 'Premium 6');
  if (program === 4) return serviceLabel(t, 'turfProgramUltra6', 'Ultra 6');
  return null;
}

// Snow quote flags in legacy GetSnowServices order (plowing, shoveling crew,
// truck salting, crew salting, premium). [config flag, serviceNames label key].
// The crew-salting flags are labelled by their older `saltingCrew*` names.
const SNOW_CONTRACT_FLAGS = [
  ['parkingLotDrivewayPlowingEnabled', 'parkingLotDrivewayPlowingEnabled'],
  ['parkingLotParkingLotPlowingEnabled', 'parkingLotParkingLotPlowingEnabled'],
  ['shovelingCitySidewalkEnabled', 'shovelingCitySidewalkEnabled'],
  ['shovelingInternalSidewalkEnabled', 'shovelingInternalSidewalkEnabled'],
  ['shovelingDrivewayEnabled', 'shovelingDrivewayEnabled'],
  ['truckDrivewaySaltingEnabled', 'truckDrivewaySaltingEnabled'],
  ['truckParkingLotSaltingEnabled', 'truckParkingLotSaltingEnabled'],
  ['parkingSaltingEnabled', 'parkingSaltingEnabled'],
  ['crewCitySidewalkSaltingEnabled', 'saltingCrewCitySideWalksEnabled'],
  ['crewInternalSidewalkSaltingEnabled', 'saltingCrewInternalSideWalksEnabled'],
  ['crewDrivewaySaltingEnabled', 'saltingCrewDrivewaysEnabled'],
  ['citySaltingEnabled', 'citySaltingEnabled'],
  ['internalSaltingEnabled', 'internalSaltingEnabled'],
  ['drivewaySaltingEnabled', 'drivewaySaltingEnabled'],
  ['premiumSaltingEnabled', 'premiumSaltingEnabled'],
];

/**
 * Winter "Services Performed Today" list: the snow work on the contract.
 * Commercial quotes list each plow/shovel/salt line that is switched on;
 * residential quotes only carry a seasonal program (1 Basic, 2 Premium).
 */
function buildSnowServiceItems(snow, t) {
  if (!snow || typeof snow !== 'object' || snow.enabled === false) return [];
  const subs = SNOW_CONTRACT_FLAGS
    .filter(([flag]) => isTruthy(snow[flag]))
    .map(([, labelKey]) => ({
      key: `snow.${labelKey}`,
      label: serviceLabel(t, labelKey, labelKey),
      type: 'sub',
    }));
  const program = toNumber(snow.program);
  if (subs.length === 0 && program === 1) {
    subs.push({ key: 'snow.snowProgramBasic', label: serviceLabel(t, 'snowProgramBasic', 'Basic'), type: 'sub' });
  } else if (subs.length === 0 && program === 2) {
    subs.push({ key: 'snow.snowProgramPremium', label: serviceLabel(t, 'snowProgramPremium', 'Premium'), type: 'sub' });
  }
  if (subs.length === 0) return [];
  return [{ key: 'snow', label: serviceLabel(t, 'snow', 'Snow Removal'), type: 'service' }, ...subs];
}

/**
 * @param {object|string} rawServices `available_services` JSON
 * @param {{ t?: Function, customerType?: string, jobType?: string }} options
 * @returns {{ key: string, label: string, type: 'service'|'sub' }[]}
 */
export function buildContractedServiceItems(rawServices, options = {}) {
  const { t, customerType, jobType } = options;
  const services = parseServiceConfig(rawServices);
  if (!services || typeof services !== 'object') return [];

  if (jobType === 'winter') return buildSnowServiceItems(services.snow, t);

  const items = [];
  const pushService = (key, labelKey, fallback) => {
    items.push({
      key,
      label: serviceLabel(t, labelKey, fallback),
      type: 'service',
    });
  };
  const pushSub = (key, labelKey, fallback) => {
    items.push({
      key,
      label: serviceLabel(t, labelKey, fallback),
      type: 'sub',
    });
  };

  if (isSpringOnContract(services.spring)) {
    pushService('spring', 'spring', 'Spring Cleanup');
  }

  if (isLawnOnContract(services.lawn)) {
    const lawn = services.lawn;
    pushService('lawn', 'lawn', 'Lawn Care');
    if (lawn.mowing !== false) {
      pushSub('lawn.mowing', 'mowing', 'Mowing');
    }
    if (isTruthy(lawn.commonLawnAreaOnly)) {
      pushSub('lawn.commonLawnAreaOnly', 'commonLawnAreaOnly', 'Common lawn areas only');
    }
    if (isTruthy(lawn.bagging)) {
      pushSub('lawn.bagging', 'bagging', 'Bagging');
    }
    if (isTruthy(lawn.trashBagServiceEnabled)) {
      pushSub('lawn.trashBagService', 'trashBagService', 'Trash bags service');
    }
  }

  const gardening = services.gardening;
  if (isGardeningOnContract(gardening)) {
    pushService('gardening', 'gardening', 'Gardening');
    if (gardeningSubOnContract(gardening.deWeedingWorkTime, gardening.deWeeding)) {
      pushSub('gardening.deWeeding', 'deWeeding', 'De-weeding');
    }
    if (gardeningSubOnContract(gardening.edgeWorkTime, gardening.edge)) {
      pushSub('gardening.edging', 'edging', 'Edging');
    }
    if (gardeningSubOnContract(gardening.soilTillingWorkTime, gardening.soilTilling)) {
      pushSub('gardening.soilTilling', 'soilTilling', 'Soil Tilling');
    }
    if (isTruthy(gardening.commonOnly)) {
      pushSub('gardening.commonOnly', 'commonOnly', 'Common area gardens only');
    }
    if (isTruthy(gardening.weedSelected)) {
      pushSub('gardening.weedControl', 'weedControl', 'Weed Control');
    }
    if (isTruthy(gardening.mulchSelected)) {
      pushSub('gardening.mulch', 'mulching', 'Mulching');
    }
    if (isTruthy(gardening.frontHouseSelected)) {
      pushSub('gardening.frontHouse', 'frontHouse', 'Garden front house');
    }
    if (isTruthy(gardening.sideHouseSelected)) {
      pushSub('gardening.sideHouse', 'sideHouse', 'Garden side house');
    }
    if (isTruthy(gardening.backHouseSelected)) {
      pushSub('gardening.backHouse', 'backHouse', 'Garden back house');
    }
    if (isTruthy(gardening.frontCondoSelected)) {
      pushSub('gardening.frontCondo', 'frontCondo', 'Garden front condo');
    }
    if (isTruthy(gardening.backCondoSelected)) {
      pushSub('gardening.backCondo', 'backCondo', 'Garden back condo');
    }
    if (isTruthy(gardening.commonTreeRingsSelected)) {
      pushSub('gardening.treeRings', 'treeRings', 'Tree Rings');
    }
  }

  if (isTrimmingOnContract(services.trimming)) {
    pushService('trimming', 'trimming', 'Trimming');
  }

  if (isTurfOnContract(services.turf)) {
    pushService('turf', 'turf', 'Turf Treatment');
    const program = getTurfProgramLabel(t, services.turf);
    if (program) {
      items.push({ key: 'turf.program', label: program, type: 'sub' });
    }
  }

  if (isFallOnContract(services.fall)) {
    pushService('fall', 'fall', 'Fall Cleanup');
  }

  if (isSweepingOnContract(services.sweeping, customerType, services)) {
    pushService('sweeping', 'sweeping', 'Parking Lot Sweeping');
  }

  return items;
}

export function normalizeCheckedServices(raw) {
  if (!raw) return {};
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      return {};
    }
  }

  const asObject = {};
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      if (!item) return;
      if (typeof item === 'string') {
        asObject[item] = true;
      } else if (typeof item === 'object') {
        const key = item.key || item.name || item.label;
        if (key) asObject[key] = true;
      }
    });
  } else if (parsed && typeof parsed === 'object') {
    Object.assign(asObject, parsed);
  }

  Object.entries(CHECKED_KEY_ALIASES).forEach(([from, to]) => {
    if (asObject[from] && asObject[to] == null) {
      asObject[to] = asObject[from];
    }
  });

  return asObject;
}

export function isServiceChecked(checked, key) {
  const values = normalizeCheckedServices(checked);
  if (values[key] === true || values[key] === 'true') return true;
  const alias = Object.keys(CHECKED_KEY_ALIASES).find((from) => CHECKED_KEY_ALIASES[from] === key);
  if (alias && (values[alias] === true || values[alias] === 'true')) return true;
  return false;
}
